/**
 * Installierbare App (PWA): Service Worker, Update-Hinweis, Installieren-Angebot.
 *
 * Der Update-Hinweis ist kein Beiwerk: Ohne ihn arbeitet jemand unter Umständen
 * wochenlang mit einer alten Anwendungsversion weiter, weil der Browser die
 * installierte Fassung behält. Die neue Version wird erst auf Klick aktiv –
 * sonst würde ein halb ausgefülltes Meldeformular neu geladen.
 */

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstall: InstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

/** Läuft die Anwendung als installierte App (eigenes Fenster)? */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad – dort gibt es kein Installieren-Angebot, nur „Zum Home-Bildschirm“. */
export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Bietet der Browser gerade „App installieren“ an (Chrome, Edge, Android)? */
export function installAvailable(): boolean {
  return deferredInstall !== null;
}

export function onInstallAvailabilityChange(listener: () => void): () => void {
  installListeners.add(listener);
  return () => installListeners.delete(listener);
}

export async function promptInstall(): Promise<boolean> {
  const event = deferredInstall;
  if (!event) return false;
  deferredInstall = null;
  installListeners.forEach((l) => l());
  await event.prompt();
  return (await event.userChoice).outcome === 'accepted';
}

/** Zahl am App-Symbol (z. B. nicht zugewiesene Tickets); 0 entfernt sie. */
export function setAppBadge(count: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  if (count > 0) void nav.setAppBadge?.(count).catch(() => undefined);
  else void nav.clearAppBadge?.().catch(() => undefined);
}

export function registerServiceWorker(navigate: (url: string) => void): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstall = event as InstallPromptEvent;
    installListeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferredInstall = null;
    installListeners.forEach((l) => l());
  });

  if (!('serviceWorker' in navigator)) return;

  // Antippen einer Benachrichtigung: Die offene App wechselt zum Ticket.
  navigator.serviceWorker.addEventListener('message', (event) => {
    const data = event.data as { type?: string; url?: string } | undefined;
    if (data?.type === 'navigate' && data.url?.startsWith('/')) navigate(data.url);
  });

  // Im Entwicklungsbetrieb würde ein Service Worker das Neuladen behindern.
  if (import.meta.env.DEV) return;

  let userAccepted = false;
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        const offer = (worker: ServiceWorker) =>
          showUpdateHint(() => {
            userAccepted = true;
            worker.postMessage({ type: 'SKIP_WAITING' });
          });
        if (registration.waiting && navigator.serviceWorker.controller) offer(registration.waiting);
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // „installed“ mit vorhandenem Controller heißt: Es gibt eine neuere Fassung.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) offer(installing);
          });
        });
        // Installierte Apps laufen oft tagelang: regelmäßig und beim Zurückkehren nach Updates sehen.
        const check = () => void registration.update().catch(() => undefined);
        setInterval(check, 60 * 60_000);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') check();
        });
      })
      .catch(() => {
        /* Ohne Service Worker läuft die Anwendung normal weiter. */
      });

    // Nach dem Wechsel auf die neue Version einmal neu laden – nur wenn die Person es wollte.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!userAccepted) return;
      userAccepted = false;
      window.location.reload();
    });
  });
}

function showUpdateHint(onReload: () => void): void {
  if (document.getElementById('ad-update-hint')) return;

  const bar = document.createElement('div');
  bar.id = 'ad-update-hint';
  bar.setAttribute('role', 'status');
  // Auf dem Handy über der unteren Navigationsleiste (64 px) stehen, nicht darauf.
  const bottom = window.matchMedia('(max-width: 767px)').matches ? 80 : 24;
  bar.style.cssText = `
    position:fixed;left:50%;bottom:calc(${bottom}px + env(safe-area-inset-bottom));transform:translateX(-50%);z-index:900;
    display:flex;align-items:center;justify-content:space-between;gap:16px;width:calc(100vw - 32px);max-width:440px;box-sizing:border-box;
    background:#1f262b;color:#fff;padding:12px 12px 12px 18px;
    font:500 16px/1.4 Barlow,system-ui,sans-serif;box-shadow:0 12px 40px rgb(20 25 29 / .28)`;

  const text = document.createElement('span');
  text.textContent = 'Eine neue Version ist da.';

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Jetzt laden';
  button.style.cssText = `
    background:#e20026;color:#fff;border:0;min-height:44px;padding:0 18px;white-space:nowrap;
    font:600 15px/1 'Barlow Semi Condensed',Barlow,system-ui,sans-serif;letter-spacing:.05em;
    text-transform:uppercase;cursor:pointer`;
  button.addEventListener('click', () => {
    button.disabled = true;
    button.textContent = 'Lädt …';
    onReload();
    // Falls der Wechsel ausbleibt (alter Service Worker ohne SKIP_WAITING): hart neu laden.
    setTimeout(() => window.location.reload(), 4000);
  });

  bar.append(text, button);
  document.body.append(bar);
}
